export type Country = "pakistan" | "india";

export const SITE_NAME = "DrHint";
export const SITE_TAGLINE = "Live 24K, 22K & 21K gold prices for Pakistan and India, updated every 10 minutes";

export type CityInfo = {
  slug: string;
  name: string;
  /** Premium over the country-base price as a multiplier (e.g. 1.002 = +0.2%) */
  premium: number;
};

export const PAKISTAN_CITIES: CityInfo[] = [
  { slug: "karachi", name: "Karachi", premium: 1.0000 },
  { slug: "lahore", name: "Lahore", premium: 1.0008 },
  { slug: "islamabad", name: "Islamabad", premium: 1.0012 },
  { slug: "rawalpindi", name: "Rawalpindi", premium: 1.0012 },
  { slug: "peshawar", name: "Peshawar", premium: 1.0015 },
  { slug: "quetta", name: "Quetta", premium: 1.0020 },
  { slug: "multan", name: "Multan", premium: 1.0010 },
  { slug: "faisalabad", name: "Faisalabad", premium: 1.0010 },
  { slug: "hyderabad-pk", name: "Hyderabad", premium: 1.0005 },
  { slug: "sialkot", name: "Sialkot", premium: 1.0012 },
  { slug: "gujranwala", name: "Gujranwala", premium: 1.0010 },
  { slug: "bahawalpur", name: "Bahawalpur", premium: 1.0014 },
  { slug: "sargodha", name: "Sargodha", premium: 1.0012 },
  { slug: "sahiwal", name: "Sahiwal", premium: 1.0012 },
  { slug: "sheikhupura", name: "Sheikhupura", premium: 1.0010 },
  { slug: "sukkur", name: "Sukkur", premium: 1.0015 },
  { slug: "larkana", name: "Larkana", premium: 1.0015 },
  { slug: "mardan", name: "Mardan", premium: 1.0018 },
  { slug: "abbottabad", name: "Abbottabad", premium: 1.0018 },
  { slug: "toba-tek-singh", name: "Toba Tek Singh", premium: 1.0015 },
  { slug: "jhang", name: "Jhang", premium: 1.0015 },
];

export const INDIA_CITIES: CityInfo[] = [
  { slug: "delhi", name: "Delhi", premium: 1.0012 },
  { slug: "mumbai", name: "Mumbai", premium: 1.0000 },
  { slug: "chennai", name: "Chennai", premium: 1.0025 },
  { slug: "kolkata", name: "Kolkata", premium: 1.0008 },
  { slug: "bangalore", name: "Bangalore", premium: 1.0020 },
  { slug: "hyderabad-in", name: "Hyderabad", premium: 1.0018 },
  { slug: "ahmedabad", name: "Ahmedabad", premium: 1.0005 },
  { slug: "pune", name: "Pune", premium: 1.0003 },
  { slug: "jaipur", name: "Jaipur", premium: 1.0015 },
  { slug: "lucknow", name: "Lucknow", premium: 1.0015 },
  { slug: "surat", name: "Surat", premium: 1.0005 },
  { slug: "kanpur", name: "Kanpur", premium: 1.0016 },
  { slug: "nagpur", name: "Nagpur", premium: 1.0004 },
  { slug: "indore", name: "Indore", premium: 1.0014 },
  { slug: "bhopal", name: "Bhopal", premium: 1.0015 },
  { slug: "patna", name: "Patna", premium: 1.0022 },
  { slug: "vadodara", name: "Vadodara", premium: 1.0005 },
  { slug: "ludhiana", name: "Ludhiana", premium: 1.0016 },
  { slug: "coimbatore", name: "Coimbatore", premium: 1.0025 },
  { slug: "visakhapatnam", name: "Visakhapatnam", premium: 1.0020 },
];

export function getCities(country: Country): CityInfo[] {
  return country === "pakistan" ? PAKISTAN_CITIES : INDIA_CITIES;
}

export function findCity(country: Country, slug: string): CityInfo | undefined {
  return getCities(country).find((c) => c.slug === slug);
}

// Conversion constants
export const GRAMS_PER_OZ = 31.1035;
export const GRAMS_PER_TOLA = 11.6638;
export const PURITY_22K = 22 / 24; // 0.9167
export const PURITY_21K = 21 / 24; // 0.8750
export const PURITY_18K = 18 / 24; // 0.7500
export const PURITY_14K = 14 / 24; // 0.5833

export const KARATS = ["24k", "22k", "21k", "18k", "14k"] as const;
export type Karat = (typeof KARATS)[number];
export function karatPurity(k: Karat): number {
  return k === "24k"
    ? 1
    : k === "22k"
      ? PURITY_22K
      : k === "21k"
        ? PURITY_21K
        : k === "18k"
          ? PURITY_18K
          : PURITY_14K;
}
export function karatLabel(k: Karat): string {
  return k.replace("k", "K").toUpperCase();
}

// Pure arithmetic model — fixed local-market premiums added on top of the
// converted international spot price. No clamps, no multiplier anchors.
export const LOCAL_PREMIUM_PK = 4000; // Rs per tola (24K gold, Pakistan)
export const LOCAL_PREMIUM_IN = 11000; // Rs per 10g (24K gold, India)

// India: 10% multiplier on top of (spot×FX + flat premium) to cover the
// import-duty + GST gap (≈15% customs + 3% GST). Flat ₹11,000 retains
// regional dealer margin; this multiplier brings the rate in line with
// observed retail bullion prices.
export const INDIA_DUTY_MULTIPLIER = 1.10;

// Oz/tola ratio used by the per-tola arithmetic formula (per spec: 2.6667).
export const OZ_PER_TOLA_FORMULA = 2.6667;

/** Identity passthrough retained so legacy `clamp` imports keep compiling. */
export function clamp(_min: number, _max: number, value: number): number {
  return value;
}

/** Pure deterministic karat derivation from the 24K baseline. */
export function convertKarat(base24k: number, karat: number): number {
  return base24k * (karat / 24);
}

// oz / tola = 31.1035 / 11.6638 ≈ 2.6667 (matches the 2.67 the user specified)
export const OZ_PER_TOLA = GRAMS_PER_OZ / GRAMS_PER_TOLA;
// oz / 10g = 3.11035
export const OZ_PER_10G = GRAMS_PER_OZ / 10;

export function formatMoney(value: number, currency: "PKR" | "INR"): string {
  const symbol = currency === "PKR" ? "Rs " : "₹";
  const safe = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return symbol + Math.round(safe).toLocaleString(currency === "PKR" ? "en-PK" : "en-IN");
}

export type CityPrices = {
  perGram24: number;
  perGram22: number;
  perGram21: number;
  perTola24: number;
  perTola22: number;
  perTola21: number;
  per10g24: number;
  per10g22: number;
  per10g21: number;
};

export function computeCityPrices(args: {
  spotUsdPerOz: number;
  fxRate: number; // local currency per 1 USD
  country: Country;
  cityPremium: number;
}): CityPrices {
  // Defensive coercion: any missing/NaN input collapses to 0 so downstream
  // .toFixed / .toLocaleString never receive NaN (Googlebot crash guard).
  const safeSpot = Number.isFinite(args.spotUsdPerOz) ? args.spotUsdPerOz : 0;
  const safeFx = Number.isFinite(args.fxRate) ? args.fxRate : 0;
  const safePremium = Number.isFinite(args.cityPremium) && args.cityPremium > 0 ? args.cityPremium : 1;
  let base24: number; // per-tola for PK, per-10g for IN
  if (args.country === "pakistan") {
    base24 = (safeSpot / OZ_PER_TOLA_FORMULA) * safeFx + LOCAL_PREMIUM_PK;
  } else {
    base24 = (safeSpot / GRAMS_PER_OZ) * safeFx * 10 + LOCAL_PREMIUM_IN;
    base24 = base24 * INDIA_DUTY_MULTIPLIER;
  }
  base24 = base24 * safePremium;
  if (!Number.isFinite(base24) || safeSpot === 0 || safeFx === 0) base24 = 0;
  const perGram24 = args.country === "pakistan" ? base24 / GRAMS_PER_TOLA : base24 / 10;
  const perGram22 = convertKarat(perGram24, 22);
  const perGram21 = convertKarat(perGram24, 21);
  return {
    perGram24,
    perGram22,
    perGram21,
    perTola24: perGram24 * GRAMS_PER_TOLA,
    perTola22: perGram22 * GRAMS_PER_TOLA,
    perTola21: perGram21 * GRAMS_PER_TOLA,
    per10g24: perGram24 * 10,
    per10g22: perGram22 * 10,
    per10g21: perGram21 * 10,
  };
}

export function currencyFor(country: Country): "PKR" | "INR" {
  return country === "pakistan" ? "PKR" : "INR";
}